import { CommonModule } from '@angular/common'
import { Component, Input, ViewChild } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { E11DynamicFormModule, e11LayoutGroup } from '@engineering11/ui-lib/e11-form-builder'
import { E11SwitchModule } from '@engineering11/ui-lib/e11-switch'
import { TranslateModule } from '@ngx-translate/core'
import { IAutomationFormConfig } from '../automation-form-message'
import { IAutomatedReminderMessages } from '../automation-request-updates/auto-request-updates.model'
import { ISendMessageFormData } from '../job-automation.model'
import { AutomationMessageFormComponent } from './automation-message-form.component'
import { FormField } from './automation-reset.component'
import { AutomationTypeDisplayNames } from './automation-upgrade-prompt.component'

type IAutomationReminderMessageSection = {
  data: ISendMessageFormData | undefined
  showEnableSwitch: boolean
  fieldName: FormField
  footNoteText?: string
  extraFieldGroups?: ReturnType<typeof e11LayoutGroup<any>>[]
  delayTimeHeaderText?: string
}

@Component({
  selector: 'automation-reminder-messages',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, E11DynamicFormModule, E11SwitchModule, TranslateModule, AutomationMessageFormComponent],
  template: `
    <div class="e11-flex e11-flex-col e11-gap-16">
      <!-- Initial Message -->
      <automation-message-form
        #initialMessageRef
        [automationTypeDisplayName]="automationTypeDisplayName"
        headerText="Initial Message"
        [loading]="loading"
        [showEnableSwitch]="initialMessageConfig.showEnableSwitch"
        [formConfig]="formConfig"
        [formData]="initialMessageConfig.data"
        [showResetButton]="showResetButton"
        [formFieldName]="initialMessageConfig.fieldName"
        [footNoteMessageType]="initialMessageConfig.footNoteText ? 'text' : 'reminder-info'"
        [footNoteText]="initialMessageConfig.footNoteText"
        [extraFieldGroups]="initialMessageConfig.extraFieldGroups"
        [delayTimeHeaderText]="initialMessageConfig.delayTimeHeaderText"
        [showImmediateSwitch]="showImmediateSwitch"
        [immediateValue]="immediateValue"
        (immediateChange)="immediateValue = $event"
      >
        <ng-content select="initial-message-extra-field-groups"></ng-content>
      </automation-message-form>

      <!-- Follow Up -->
      <automation-message-form
        #followUpRef
        [automationTypeDisplayName]="automationTypeDisplayName"
        headerText="Follow up"
        [loading]="loading"
        [showEnableSwitch]="followUpMessageConfig.showEnableSwitch"
        [formConfig]="formConfig"
        [formData]="followUpMessageConfig.data"
        [showResetButton]="showResetButton"
        [formFieldName]="followUpMessageConfig.fieldName"
        [footNoteMessageType]="followUpMessageConfig.footNoteText ? 'text' : 'reminder-info'"
        [footNoteText]="followUpMessageConfig.footNoteText"
        [extraFieldGroups]="followUpMessageConfig.extraFieldGroups"
        [delayTimeHeaderText]="followUpMessageConfig.delayTimeHeaderText"
      >
        <ng-content select="follow-up-message-extra-field-groups"></ng-content>
      </automation-message-form>

      <!-- Warning -->
      <automation-message-form
        #warningRef
        [automationTypeDisplayName]="automationTypeDisplayName"
        headerText="Warning"
        [loading]="loading"
        [showEnableSwitch]="warningMessageConfig.showEnableSwitch"
        [formConfig]="formConfig"
        [formData]="warningMessageConfig.data"
        [showResetButton]="showResetButton"
        [formFieldName]="warningMessageConfig.fieldName"
        [footNoteMessageType]="warningMessageConfig.footNoteText ? 'text' : 'reminder-info'"
        [footNoteText]="warningMessageConfig.footNoteText"
        [extraFieldGroups]="warningMessageConfig.extraFieldGroups"
      >
        <ng-content select="warning-message-extra-field-groups"></ng-content>
      </automation-message-form>
    </div>
  `,
})
export class AutomationReminderMessagesComponent {
  @Input({ required: true }) automationTypeDisplayName!: AutomationTypeDisplayNames
  @Input() loading = false
  @Input() showResetButton = false
  @Input() formConfig?: IAutomationFormConfig

  @ViewChild('initialMessageRef') initialMessageRef!: AutomationMessageFormComponent
  @ViewChild('followUpRef') followUpRef!: AutomationMessageFormComponent
  @ViewChild('warningRef') warningRef!: AutomationMessageFormComponent

  @Input({ required: true }) initialMessageConfig!: IAutomationReminderMessageSection
  @Input({ required: true }) followUpMessageConfig!: IAutomationReminderMessageSection
  @Input({ required: true }) warningMessageConfig!: IAutomationReminderMessageSection
  @Input() showImmediateSwitch = false
  @Input() immediateValue?: boolean

  getValue(): IAutomatedReminderMessages {
    return {
      immediate: this.immediateValue,
      initialMessage: !this.immediateValue
        ? this.initialMessageRef.value
        : { ...this.initialMessageRef.value, delayDuration: 0, delayUnits: 'minutes', senderId: '' },
      followUp: this.followUpRef.value,
      warning: this.warningRef.value,
    }
  }

  validate(): boolean {
    const forms = [this.initialMessageRef?.formRef, this.followUpRef?.formRef, this.warningRef?.formRef].filter(f => f)
    forms.forEach(form => form.onSubmitClick())
    return forms.every(form => form.form.valid)
  }
}
